import { Button } from "@/modules/shadcn/ui/button";
import {
  AutoAnnotationBatch,
  AutoAnnotationBatchStatusEnum,
} from "@repo/schema";
import { CircleAlert, CircleCheck, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  BATCH_DISMISS,
  BATCH_DONE_BODY,
  BATCH_RETRY_FAILED,
  BATCH_RUNNING_BODY,
  batchDoneTitle,
  batchRunningTitle,
} from "../BatchAutoAnnotateDialog/copy";

export interface BatchRunCardProps {
  batch: AutoAnnotationBatch;
  onDismiss: () => void;
  /** Re-selects the failed images so the operator can run them again. */
  onRetryFailed: (taskIds: number[]) => void;
}

/**
 * Batch progress, in the same floating slot as the single-run panel.
 *
 * A sibling of PreAnnotateRunPanel rather than a variant of it: that component
 * exists to host a Robopipe Vision conversation — feedback, transcript, the
 * learned-note diff — none of which a batch has. Sharing only the card shell
 * keeps the feedback flow from growing a second, unrelated state machine.
 *
 * There is no Cancel. With an unbounded fan-out every image is already in
 * flight when this first renders, and aborting the HTTP request does not stop
 * the model — the turn finishes server-side and stages its boxes anyway. A
 * button promising otherwise would be contradicted by the results arriving.
 */
export const BatchRunCard = ({
  batch,
  onDismiss,
  onRetryFailed,
}: BatchRunCardProps) => {
  const { t } = useTranslation(["label", "common"]);
  const running = batch.status === AutoAnnotationBatchStatusEnum.RUNNING;
  const failedTaskIds = batch.items
    .filter((item) => item.status === "failed")
    .map((item) => item.taskId);

  return (
    /*
      The card owns its own positioning, like AwaitingReviewBanner and
      PreAnnotateRunPanel — and centres the CARD, not a wrapper around it.
      A wrapper with no width of its own sizes to the body text's max-content
      (~780px), centres that, and leaves the 560px card pinned to its left
      edge: correctly centred box, visibly off-centre card.
    */
    <div
      data-slot="batch-run-card"
      // Lifted clear of the label chip bar, like the sibling cards.
      className="pointer-events-none absolute inset-x-3 bottom-[84px] z-20 flex justify-center"
    >
      <div
        role="status"
        className="pointer-events-auto flex w-[min(560px,100%)] flex-col rounded-[14px] bg-white shadow-[0_10px_32px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.08)] animate-in fade-in slide-in-from-bottom-2 duration-200"
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-4">
          <span className="flex min-w-0 items-center gap-2.5">
            {running ? (
              <Loader2 className="size-[18px] shrink-0 animate-spin text-primary" aria-hidden />
            ) : failedTaskIds.length > 0 ? (
              <CircleAlert className="size-[18px] shrink-0 text-amber-600" aria-hidden />
            ) : (
              <CircleCheck className="size-[18px] shrink-0 text-emerald-600" aria-hidden />
            )}
            <span className="truncate text-base font-bold">
              {running
                ? batchRunningTitle(t, batch.done, batch.total)
                : batchDoneTitle(t, batch.done, batch.failed)}
            </span>
          </span>
          {!running && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t(BATCH_DISMISS)}
              className="-mr-1 size-7 shrink-0"
              onClick={onDismiss}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>

        <p className="px-5 pb-1 pt-2 text-sm text-muted-foreground">
          {running ? t(BATCH_RUNNING_BODY) : t(BATCH_DONE_BODY)}
        </p>

        {running && <BatchProgressBar batch={batch} />}

        {!running && failedTaskIds.length > 0 && (
          <div className="mt-3 flex justify-end border-t border-black/[0.08] px-5 py-3">
            <Button variant="outline" size="sm" onClick={() => onRetryFailed(failedTaskIds)}>
              {t(BATCH_RETRY_FAILED)}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Hand-rolled: there is no progress primitive in this app, and one bar is not
 * a reason to add one. Failures are drawn as their own segment rather than
 * counted as progress — a run that is 30% done and 20% failed is not 50%
 * finished in any sense the operator cares about.
 */
const BatchProgressBar = ({ batch }: { batch: AutoAnnotationBatch }) => {
  const pct = (n: number) => (batch.total === 0 ? 0 : (n / batch.total) * 100);
  return (
    <div
      className="mx-5 mb-4 mt-2 flex h-1.5 overflow-hidden rounded-full bg-black/[0.08]"
      role="progressbar"
      aria-valuenow={batch.done + batch.failed}
      aria-valuemin={0}
      aria-valuemax={batch.total}
    >
      <div className="bg-emerald-500 transition-all" style={{ width: `${pct(batch.done)}%` }} />
      <div className="bg-amber-500 transition-all" style={{ width: `${pct(batch.failed)}%` }} />
    </div>
  );
};
